import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PILLARS } from "@/constants/pillars";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Check, X } from "lucide-react";
import PayoutsPanel from "@/components/PayoutsPanel";
import FeedbackPanel from "@/components/FeedbackPanel";
import UsersPanel from "@/components/UsersPanel";
import AffiliazioneAdminPanel from "@/components/AffiliazioneAdminPanel";
import CashbackAdminPanel from "@/components/CashbackAdminPanel";
import { useI18n } from "@/context/I18nContext";

const STAGE_LABELS = {
  lead_in: "Lead", in_progress: "In corso", assigned: "Assegnato",
  completed: "Completato", commission_paid: "Provvigione pagata",
};

function UserPipeline() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", pillar: "asset", budget: "" });

  const load = async () => {
    const { data } = await api.get("/projects"); setProjects(data);
    if (user.role === "specialist") { try { const { data: a } = await api.get("/assignments/mine"); setAssignments(a); } catch {} }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    try {
      await api.post("/projects", {
        title: form.title, description: form.description, pillars: [form.pillar],
        categories: {}, budget: parseFloat(form.budget) || 0,
      });
      setOpen(false); setForm({ title: "", description: "", pillar: "asset", budget: "" });
      toast.success("Richiesta inviata"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const advance = async (aid, stage) => {
    await api.patch(`/assignments/${aid}/stage`, { stage }); toast.success("Stadio aggiornato"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-2xl font-bold">{t("projects")}</h1>
        <Button data-testid="create-project-btn" onClick={() => setOpen(true)} className="h-10 rounded-full bg-black text-white text-sm"><Plus size={16} className="mr-1" />{t("request")}</Button>
      </div>

      {user.role === "specialist" && assignments.length > 0 && (
        <div className="mb-6">
          <h2 className="font-display font-bold mb-2 text-sm uppercase tracking-wide text-neutral-500">I miei incarichi</h2>
          <div className="space-y-2">
            {assignments.map((a) => (
              <div key={a.id} data-testid={`assignment-${a.id}`} className="border border-neutral-200 rounded-xl p-3">
                <p className="font-semibold text-sm">{a.project?.title}</p>
                <Select value={a.stage} onValueChange={(v) => advance(a.id, v)}>
                  <SelectTrigger data-testid={`assignment-stage-${a.id}`} className="h-9 mt-2 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="font-display font-bold mb-2 text-sm uppercase tracking-wide text-neutral-500">Le mie richieste</h2>
      {projects.length === 0 ? <p className="text-center text-neutral-400 py-8 text-sm">Nessuna richiesta ancora.</p> :
        <div className="space-y-2">
          {projects.map((p) => (
            <div key={p.id} data-testid={`project-${p.id}`} onClick={() => navigate(`/pipeline/${p.id}`)} className="border border-neutral-200 rounded-xl p-3 cursor-pointer hover:border-black transition-colors">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm">{p.title}</p>
                <span className="text-[11px] bg-neutral-100 rounded-full px-2 py-0.5">{STAGE_LABELS[p.stage]}</span>
              </div>
              <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{p.description}</p>
              {p.budget > 0 && <p className="text-xs font-mono mt-1">Budget: €{p.budget}</p>}
            </div>
          ))}
        </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Nuova richiesta</DialogTitle></DialogHeader>
          <Input data-testid="project-title" placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11" />
          <Textarea data-testid="project-desc" placeholder="Descrivi cosa ti serve" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2">
            <Select value={form.pillar} onValueChange={(v) => setForm({ ...form, pillar: v })}>
              <SelectTrigger data-testid="project-pillar" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{PILLARS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="project-budget" type="number" placeholder="Budget €" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="h-11 w-32" />
          </div>
          <Button data-testid="project-submit" onClick={create} className="w-full h-11 rounded-full bg-black text-white">Invia richiesta</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminPipeline() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [projects, setProjects] = useState([]);
  const [apps, setApps] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [members, setMembers] = useState([]);
  const [grantEmail, setGrantEmail] = useState("");

  const load = async () => {
    const [d, p, a, r, m] = await Promise.all([
      api.get("/dashboard"), api.get("/projects"),
      api.get("/applications"), api.get("/revenue"), api.get("/gold/members"),
    ]);
    setDash(d.data); setProjects(p.data); setApps(a.data); setRevenue(r.data); setMembers(m.data);
  };
  useEffect(() => { load(); }, []);

  const grant = async () => { try { await api.post("/gold/grant", { email: grantEmail }); toast.success("Oro concesso"); setGrantEmail(""); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const revoke = async (email) => { await api.post("/gold/revoke", { email }); toast.success("Revocato"); load(); };
  const resetData = async () => {
    if (!window.confirm("Sei sicuro? Verranno cancellati TUTTI i dati (post, offerte, utenti registrati, ecc.) tranne il tuo account admin. Operazione irreversibile.")) return;
    try { const { data } = await api.post("/admin/reset-data"); toast.success("Dati azzerati"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-bold">{t("crm_pipeline")}</h1>

      <div className="grid grid-cols-2 gap-3">
        <div className="border border-neutral-200 rounded-2xl p-4"><p className="text-xs text-neutral-500">Ricavi totali</p><p className="font-display text-2xl font-extrabold">€{dash?.total_revenue?.toFixed(2)}</p></div>
        <div className="border border-neutral-200 rounded-2xl p-4"><p className="text-xs text-neutral-500">Utenti</p><p className="font-display text-2xl font-extrabold">{dash?.total_users}</p></div>
      </div>

      <div>
        <h2 className="font-display font-bold mb-2">Stato dei progetti</h2>
        <div className="grid grid-cols-5 gap-1.5">
          {dash && Object.entries(dash.pipeline).map(([k, v]) => (
            <div key={k} className="bg-neutral-100 rounded-xl p-2 text-center">
              <p className="font-display font-bold text-lg">{v}</p>
              <p className="text-[9px] text-neutral-500 leading-tight">{STAGE_LABELS[k]}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display font-bold mb-2">Progetti ({projects.length})</h2>
        <div className="space-y-2">
          {projects.slice(0, 8).map((p) => (
            <div key={p.id} data-testid={`admin-project-${p.id}`} onClick={() => navigate(`/pipeline/${p.id}`)} className="border border-neutral-200 rounded-xl p-3 flex items-center justify-between cursor-pointer hover:border-black transition-colors">
              <div><p className="font-semibold text-sm">{p.title}</p><p className="text-xs text-neutral-500">{p.author?.name}</p></div>
              <span className="text-[11px] bg-neutral-100 rounded-full px-2 py-0.5">{STAGE_LABELS[p.stage]}</span>
            </div>
          ))}
        </div>
      </div>


      <div>
        <h2 className="font-display font-bold mb-2">Candidature ({apps.length})</h2>
        <div className="space-y-2">
          {apps.slice(0, 6).map((a) => (
            <div key={a.id} className="border border-neutral-200 rounded-xl p-3">
              <p className="font-semibold text-sm">{a.name} · <span className="text-neutral-500 font-normal">{a.type}</span></p>
              <p className="text-xs text-neutral-500">{a.email} · {a.phone}</p>
              <p className="text-xs mt-1">{a.message}</p>
            </div>
          ))}
          {apps.length === 0 && <p className="text-xs text-neutral-400">Nessuna candidatura.</p>}
        </div>
      </div>

      <div>
        <h2 className="font-display font-bold mb-2">Partner Oro</h2>
        <div className="flex gap-2 mb-2">
          <Input data-testid="grant-email" placeholder="Email utente" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} className="h-10" />
          <Button data-testid="grant-gold-btn" onClick={grant} className="h-10 rounded-full bg-[#d4af37] text-black px-4 font-semibold">Concedi Oro</Button>
        </div>
        <div className="space-y-1.5">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between border border-neutral-200 rounded-xl p-2.5">
              <span className="text-sm">{m.name} · <span className="text-neutral-400 text-xs">{m.email}</span></span>
              <button data-testid={`revoke-${m.id}`} onClick={() => revoke(m.email)} className="text-xs text-red-500">Revoca</button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display font-bold mb-2">Ricavi per fonte</h2>
        <div className="border border-neutral-200 rounded-2xl p-4 space-y-1.5">
          {revenue && Object.entries(revenue.by_source).map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm"><span className="capitalize text-neutral-600">{k}</span><span className="font-mono font-semibold">€{v.toFixed(2)}</span></div>
          ))}
          {revenue && Object.keys(revenue.by_source).length === 0 && <p className="text-xs text-neutral-400">Nessun ricavo ancora.</p>}
        </div>
      </div>

      <PayoutsPanel />

      <AffiliazioneAdminPanel />
      <CashbackAdminPanel />

      <UsersPanel />

      <FeedbackPanel />

      <div>
        <h2 className="font-display font-bold mb-2 text-red-600">Zona pericolosa</h2>
        <div className="border border-red-200 rounded-2xl p-4">
          <p className="text-sm text-neutral-600 mb-3">Azzera tutti i dati di test (post, offerte, prenotazioni, utenti registrati, notifiche, ecc.). Rimane solo il tuo account admin. Utile per ripartire da zero dopo la pubblicazione.</p>
          <Button data-testid="reset-data-btn" onClick={resetData} className="w-full h-11 rounded-full bg-red-600 hover:bg-red-700 text-white">Rimuovi tutti i dati</Button>
        </div>
      </div>
    </div>
  );
}

export default function PipelinePage() {
  const { user } = useAuth();
  return <div className="p-4">{user?.role === "admin" ? <AdminPipeline /> : <UserPipeline />}</div>;
}
